/**
 * HelpTip
 * -------------------------------------------------------------
 * Tiny `?` button that opens an accessible tooltip popover with
 * a title, body, and optional example. Works on hover OR click
 * so it's usable on both desktop and touch devices.
 *
 *   <HelpTip
 *     title="Token cost"
 *     body="How many tokens a member spends to redeem this item."
 *     example="A coffee at 3 tokens means a member with 5 tokens can redeem one and have 2 left."
 *   />
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import './HelpTip.css';

let _seq = 0;
function nextId() {
  _seq += 1;
  return `helptip-${_seq}`;
}

export function HelpTip({ title, body, example, label, className = '' }) {
  const [open, setOpen] = useState(false);
  const idRef = useRef(nextId());
  const wrapperRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDocClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDocClick);
    };
  }, [open, close]);

  return (
    <span
      ref={wrapperRef}
      className={`helptip ${open ? 'helptip--open' : ''} ${className}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="helptip__trigger"
        aria-label={label || `Help: ${title}`}
        aria-expanded={open}
        aria-describedby={open ? idRef.current : undefined}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        <FontAwesomeIcon icon={faCircleQuestion} />
      </button>
      {open && (
        <span
          role="tooltip"
          id={idRef.current}
          className="helptip__popover"
        >
          {title && <span className="helptip__title">{title}</span>}
          {body && <span className="helptip__body">{body}</span>}
          {example && (
            <span className="helptip__example">
              <strong>Example: </strong>{example}
            </span>
          )}
        </span>
      )}
    </span>
  );
}

export default HelpTip;
